import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  VStack,
  Text,
  Spinner,
  Circle,
  Button,
  Link,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import { BookingFormData, PrenotazioneDetails } from "../types/Booking";
import { Evento } from "../types/Event";
import BookingModal from "../components/BookingModal";
import { getUserInfo, ClientPrincipal } from "../authConfig";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

const EditReservation: React.FC = () => {
  const { prenotazioneId } = useParams<{ prenotazioneId: string }>();
  const navigate = useNavigate();
  const [prenotazione, setPrenotazione] = useState<PrenotazioneDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<ClientPrincipal | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      const userInfo = await getUserInfo();
      setUser(userInfo);
      setAuthChecked(true);
    };
    checkAuth();
  }, []);

  const [searchParams] = useSearchParams();
  const adminEventoId = searchParams.get("from") === "admin" ? searchParams.get("eventoId") : null;
  const isAdminEdit = !!adminEventoId && !!user;
  const backPath = isAdminEdit ? `/eventi/${adminEventoId}/prenotazioni` : user ? "/le-mie-prenotazioni" : "/";

  useEffect(() => {
    const fetchPrenotazione = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/prenotazioni/${prenotazioneId}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError("Prenotazione non trovata");
          } else {
            setError("Errore nel recupero dei dati");
          }
          return;
        }
        const data: PrenotazioneDetails = await response.json();
        setPrenotazione(data);
      } catch (err) {
        setError("Errore di connessione");
      } finally {
        setLoading(false);
      }
    };

    fetchPrenotazione();
  }, [prenotazioneId]);

  // Past-event / cancelled restrictions apply to customers only
  const blockedError = !isAdminEdit && prenotazione
    ? new Date(prenotazione.DataEvento) < new Date()
      ? "Non è possibile modificare una prenotazione per un evento già passato"
      : prenotazione.Annullata
        ? "Non è possibile modificare una prenotazione annullata"
        : null
    : null;
  const shownError = error || blockedError;

  if (loading || (adminEventoId && !user && !authChecked)) {
    return (
      <>
        <Box position="fixed" top={0} left={0} right={0} bottom={0} backgroundImage="url('/tavolata.jpg')" backgroundSize="cover" backgroundPosition="center" zIndex={-2} />
        <Box position="fixed" top={0} left={0} right={0} bottom={0} bg="blackAlpha.800" zIndex={-1} />
        <Container maxW="container.md" py={20}>
          <VStack spacing={4}>
            <Spinner size="xl" color="white" thickness="4px" />
            <Text color="white">Caricamento...</Text>
          </VStack>
        </Container>
      </>
    );
  }

  if (shownError) {
    return (
      <>
        <Box position="fixed" top={0} left={0} right={0} bottom={0} backgroundImage="url('/tavolata.jpg')" backgroundSize="cover" backgroundPosition="center" zIndex={-2} />
        <Box position="fixed" top={0} left={0} right={0} bottom={0} bg="blackAlpha.800" zIndex={-1} />
        <Container maxW="container.md" py={20}>
          <Box bg="blackAlpha.500" backdropFilter="blur(8px)" border="1px solid" borderColor="whiteAlpha.200" p={10} borderRadius="none" textAlign="center">
            <Circle size="80px" bg="red.500" color="white" mx="auto" mb={6}>
              <CloseIcon boxSize={8} />
            </Circle>
            <Text fontSize="xl" fontWeight="400" mb={4} color="white" textTransform="uppercase" letterSpacing="wide">Errore</Text>
            <Text color="gray.400" mb={6}>{shownError}</Text>
            <Link as={RouterLink} to={isAdminEdit ? backPath : "/"}>
              <Button bg="transparent" color="white" border="1px solid" borderColor="whiteAlpha.300" borderRadius="none" _hover={{ bg: "white", color: "gray.900" }}>{isAdminEdit ? "Torna all'evento" : "Torna alla home"}</Button>
            </Link>
          </Box>
        </Container>
      </>
    );
  }

  if (!prenotazione) {
    return null;
  }

  // Build pseudo Evento object from prenotazione data
  const evento: Evento = {
    EventoId: prenotazione.EventoId,
    Titolo: prenotazione.EventoTitolo,
    DataEvento: prenotazione.DataEvento,
    PrezzoAlcolico: prenotazione.PrezzoAlcolico,
    PrezzoAnalcolico: prenotazione.PrezzoAnalcolico,
    Ingredienti: prenotazione.Ingredienti || "",
    PostiTotali: prenotazione.PostiTotali,
    PostiOccupati: prenotazione.PostiOccupati,
    Menu: "",
    Attivo: true,
  };

  // Parse participant names
  let nomiPartecipanti: string[] = [];
  if (prenotazione.NomiPartecipanti) {
    try {
      nomiPartecipanti = JSON.parse(prenotazione.NomiPartecipanti);
    } catch {
      nomiPartecipanti = [];
    }
  }

  // Build initial form data from prenotazione
  const initialData: BookingFormData = {
    Nome: prenotazione.Nome,
    Cognome: prenotazione.Cognome,
    Email: prenotazione.Email,
    Telefono: prenotazione.Telefono || "",
    TipoSoggetto: prenotazione.TipoSoggetto || "Privato",
    CodiceFiscale: prenotazione.CodiceFiscale || "",
    PartitaIVA: prenotazione.PartitaIVA || "",
    RagioneSociale: prenotazione.RagioneSociale || "",
    Indirizzo: prenotazione.Indirizzo || "",
    CAP: prenotazione.CAP || "",
    Citta: prenotazione.Citta || "",
    Provincia: prenotazione.Provincia || "",
    Nazione: prenotazione.Nazione || "Italia",
    NumeroPersone: prenotazione.NumeroPersone,
    NumeroAlcolico: prenotazione.NumeroAlcolico,
    NumeroAnalcolico: prenotazione.NumeroAnalcolico,
    NomiPartecipanti: nomiPartecipanti,
    Note: prenotazione.Note || "",
    CodiceGiftCard: prenotazione.GiftCardCodice || "",
  };

  // Build initial gift card data if present
  const initialGiftCard = prenotazione.GiftCardCodice ? {
    Codice: prenotazione.GiftCardCodice,
    NumeroAlcolico: prenotazione.GiftCardNumeroAlcolico || 0,
    NumeroAnalcolico: prenotazione.GiftCardNumeroAnalcolico || 0,
  } : undefined;

  return (
    <BookingModal
      evento={evento}
      isWaitingList={false}
      isAdminView={isAdminEdit}
      onClose={() => navigate(backPath)}
      onSuccess={() => navigate(backPath)}
      editMode={true}
      editId={prenotazione.PrenotazioneId}
      editType="prenotazione"
      initialData={initialData}
      initialGiftCard={initialGiftCard}
    />
  );
};

export default EditReservation;
